import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { BlogEditor, BlogEditorPayload } from '../components/blog/BlogEditor';
import Breadcrumbs from '../components/Breadcrumbs';
import { generateSlug } from '../utils/slugUtils';
import { useBlogStore } from '../store/blogStore';
import { BlogPost } from '../types/blog';
import { toast } from 'react-toastify';
import useCreateBlog from '../hooks/Blog/useCreateBlog';
import { useUpdateBlog } from '../hooks/Blog/useUpdateBlog';

/** User records come from Cognito or our own API, which name the fields differently. */
const readStoredUser = (): Record<string, string> => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const getAuthorName = (user: Record<string, string>): string => {
  const first = user.given_name || user.firstName || '';
  const last = user.family_name || user.lastName || '';
  return `${first} ${last}`.trim() || 'Unknown Author';
};

const BlogCreatePage = () => {
  const navigate = useNavigate();
  const { addPost } = useBlogStore();
  const { createBlog } = useCreateBlog();
  const { updatePost } = useUpdateBlog();

  // Set by the first successful create. From then on this page edits that post
  // rather than creating another one, so "save draft then submit" yields one post.
  const [createdId, setCreatedId] = useState<string | null>(null);

  /** Expand the editor's payload into the full BlogPost the API expects. */
  const buildPost = (postData: BlogEditorPayload): BlogPost => {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const user = readStoredUser();

    return {
      id: '',
      _id: '',
      id_practice: user['custom:id_practice'] || '',
      title: postData.title || '',
      description: postData.description || '',
      content: postData.content || '',
      status: postData.status,
      review_results: '',
      rejection_reason: '',
      slug: generateSlug(postData.title || '') || 'untitled',
      author: getAuthorName(user),
      created_at: currentTimestamp,
      last_modified_at: currentTimestamp,
      published_at: 0,
      rejected_at: 0,
      tag: postData.tag || [],
      deleted: false,
    };
  };

  /**
   * Creates the post the first time, then updates it on every later save.
   * Returns false on failure; the caller decides what to tell the user.
   */
  const persist = async (postData: BlogEditorPayload): Promise<boolean> => {
    if (createdId) {
      return updatePost(createdId, postData as Partial<BlogPost>);
    }

    const newPost = buildPost(postData);
    const created = await createBlog(newPost);
    if (!created) return false;

    const id = created.id || created._id || null;
    setCreatedId(id);
    if (!id) {
      console.warn(
        'Create response carried no id; subsequent saves on this page will create new posts.'
      );
    }

    addPost({ ...newPost, ...created });
    return true;
  };

  const handleSave = async (postData: BlogEditorPayload): Promise<boolean> => {
    try {
      const success = await persist(postData);

      if (!success) {
        toast.error('Failed to save blog draft', {
          position: 'top-right',
          autoClose: 5000,
        });
        return false;
      }

      toast.success('Blog draft saved successfully', {
        position: 'top-right',
        autoClose: 3000,
      });
      return true;
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error('Failed to save blog draft', {
        position: 'top-right',
        autoClose: 5000,
      });
      return false;
    }
  };

  const handleSubmit = async (postData: BlogEditorPayload): Promise<boolean> => {
    try {
      const success = await persist(postData);

      if (!success) {
        toast.error('Failed to publish blog', {
          position: 'top-right',
          autoClose: 5000,
        });
        return false;
      }

      toast.success('Blog published successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/practice/blog');
      return true;
    } catch (error) {
      console.error('Error publishing blog:', error);
      toast.error('Failed to publish blog', {
        position: 'top-right',
        autoClose: 5000,
      });
      return false;
    }
  };

  const handleGoBack = () => {
    navigate('/practice/blog');
  };

  const breadcrumbItems = [
    { label: "Dashboard", href: "/practice" },
    { label: "Blog", href: "/practice/blog" },
    { label: "Create New Blog Post", href: "/practice/blog/create", active: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="hidden sm:block">
              <Breadcrumbs items={breadcrumbItems} />
            </div>
            <button
              type="button"
              onClick={handleGoBack}
              className="ml-auto inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 shadow-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back to Blog
            </button>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-white" aria-hidden="true" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight">
                Create New Blog Post
              </h1>
              <p className="mt-1 text-sm sm:text-base text-gray-600">
                Share your thoughts and expertise with your audience
              </p>
            </div>
          </div>
        </motion.div>

        {/* Editor */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
        >
          <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <BlogEditor
              mode="create"
              onSave={handleSave}
              onSubmit={handleSubmit}
            />
          </div>
        </motion.div>

        <div className="h-16 sm:h-8" />
      </div>
    </div>
  );
};

export default BlogCreatePage;
